import { useEffect, useRef } from "preact/hooks";
import type { StepProps } from "../app";
import { ContactAdvisor } from "../ContactAdvisor";
import { resolveAddress } from "../lib/api";
import { track } from "../lib/analytics";

const HINT_ID = "dac-address-hint";
const NOTICE_ID = "dac-address-notice";

/** Step 1: address in, token out. Terminal reasons render a notice here and stop. */
export function Step1Address({ state, update }: StepProps) {
  // A plain ref, not `state.busy`: a second click landing before the re-render
  // that disables this button commits still runs against the render-N closure,
  // where `state.busy` reads stale (false). A ref mutates synchronously, so it is
  // already true by the time a second click in the same tick re-enters this
  // function, and the guard below actually holds. Same shape as Step2Contact.
  // What it costs here specifically: each pass-through is another Census geocode,
  // another consumed App Check token, and a duplicated pair of
  // address_unresolved and step_complete events, which corrupts the miss rate
  // those two exist to compute.
  const submitInFlight = useRef(false);

  // One step_engage per entry to this step, matching step_view and step_complete.
  // A ref rather than state because nothing renders from it: flipping state here
  // would re-render the field mid-focus for no visible reason.
  const engagementSent = useRef(false);

  // Fires once per entry to this step (this component only mounts while
  // state.step === "address"). Without it there is no denominator for
  // address_unresolved below, so its miss RATE is not computable from what
  // is instrumented, only the raw miss count.
  useEffect(() => {
    track("step_view", { step: "address", submission_id: state.submissionId });
  }, []);

  const problem = state.blocked?.message ?? state.error ?? null;

  const onContinue = async () => {
    if (submitInFlight.current) return;
    submitInFlight.current = true;
    update({ busy: true, blocked: null, error: null });
    try {
      const out = await resolveAddress(state.address, state.submissionId);

      // The highest-value analytics event here: it yields the real Census miss
      // rate from live traffic, which is the input for deciding whether path B
      // is worth a VPC connector and per-call Maps billing at all. It therefore
      // has to fire for all four reasons, including the three that no longer
      // surface anything on this screen for the customer to notice.
      // The two terminal arms, handled identically: no token, stop here, render
      // the server's message. They stay separate branches only so the analytics
      // reason is the server's own, because out-of-area volume is a
      // marketing-targeting number and out-of-state volume is not.
      if (out.status === "out_of_state" || out.status === "outside_service_area") {
        track("address_unresolved", { reason: out.status, submission_id: state.submissionId });
        // `token: null` on every terminal arm, here and in the catch below.
        // Reaching any of them means the address now in the field was never minted
        // into a token, so any token still in state belongs to a DIFFERENT address
        // the customer has since edited (step 2's Back, then a new address).
        // Nothing reads a token while blocked today, but Step2Contact's
        // expired-token bounce nulls it for exactly this reason, and whether a
        // dead credential outlives the input it was issued against should not come
        // down to which arm happened to remember.
        update({
          busy: false,
          token: null,
          blocked: { reason: out.status, message: out.message },
        });
        return;
      }
      if (out.status === "deferred") {
        track("address_unresolved", { reason: out.reason, submission_id: state.submissionId });
      }

      // Deliberately also fired on a deferred address: the customer completed
      // this step and is on their way to step 2, so excluding it would make the
      // funnel undercount step-2 entries by exactly the miss rate.
      track("step_complete", { step: "address", submission_id: state.submissionId });
      update({ busy: false, token: out.token, step: "contact" });
    } catch {
      update({
        busy: false,
        token: null,
        error: "Something went wrong on our end. Please try again.",
      });
    } finally {
      submitInFlight.current = false;
    }
  };

  return (
    <section class="step" aria-busy={state.busy}>
      <h2>What is your address?</h2>
      <p class="hint" id={HINT_ID}>
        We check it against the New York State list of Disadvantaged Communities.
      </p>

      {/* A real <form>, not a bare button. Typing an address and pressing Enter
          is the reflex on a one-field screen, and without a form that keypress
          does nothing at all, which reads as a broken widget rather than as a
          missing affordance. `noValidate` leaves the messaging to the notice
          below rather than to a browser bubble we cannot style or announce. */}
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (state.busy || state.address.trim() === "") return;
          void onContinue();
        }}
      >
        <label class="field" for="dac-address">
          Address <span class="req" aria-hidden="true">*</span>
          <input
            id="dac-address"
            type="text"
            required
            autocomplete="street-address"
            // Not the Menands office. A real address here is answerable, and in
            // mock mode it made a canned verdict read as a plausible answer
            // rather than an obvious fixture. This is a format hint, not data.
            placeholder="Street address, city, ZIP"
            value={state.address}
            disabled={state.busy}
            aria-invalid={problem !== null}
            // Ties the hint, and any failure message, to the field itself. A
            // role="alert" paragraph is announced once when it appears; this is
            // what makes it re-readable afterwards from the input.
            aria-describedby={problem !== null ? `${HINT_ID} ${NOTICE_ID}` : HINT_ID}
            // The intent-qualified denominator for step-1 abandonment. step_view
            // fires on mount, and loader.js mounts the iframe on scroll-into-view,
            // so step_view counts readers who scrolled past with no intention of
            // using the widget. First focus is a deliberate tap that opens the
            // keyboard on a phone. See the step_engage block in the test file.
            onFocus={() => {
              if (engagementSent.current) return;
              engagementSent.current = true;
              track("step_engage", { step: "address", submission_id: state.submissionId });
            }}
            onInput={(e) => update({ address: e.currentTarget.value })}
          />
        </label>

        {state.blocked && (
          <p class="notice" id={NOTICE_ID} role="alert">
            {state.blocked.message}
          </p>
        )}
        {state.error && (
          <p class="notice" id={NOTICE_ID} role="alert">
            {state.error}
          </p>
        )}

        {/* The advisor sits in the nav row, in line with Continue. `justify-content:
            space-between` on .actions puts it at the left edge and the primary at
            the right, which is where the single-button layout already put it. */}
        <div class="actions">
          <ContactAdvisor stepName="Address" submissionId={state.submissionId} />
          <button
            type="submit"
            class="primary"
            disabled={state.busy || state.address.trim() === ""}
          >
            {state.busy ? "Checking…" : "Continue"}
          </button>
        </div>
      </form>

      {/* The button's own label changes to "Checking…", but a label change on a
          control that already has focus is not reliably announced. This is. */}
      <p class="sr-only" role="status">
        {state.busy ? "Checking the address." : ""}
      </p>
    </section>
  );
}
